import HttpResponse from "../../../utils/http/http.response";
import railyApiService from "../raily-api.service";
import userCargoOfficerListRequestFactory from "./cargo-officer/list/user-cargo-officer-list-request.factory";
import UserCargoOfficerListRequest, {
  UserCargoOfficerListRequestQueryParams,
} from "./cargo-officer/list/user-cargo-officer-list.request";
import UserCargoOfficerListResponse from "./cargo-officer/list/user-cargo-officer-list.response";
import UserCheckUserExistsRequest from "./check-user-exists/user-check-user-exists.request";
import UserCheckUserExistsResponse from "./check-user-exists/user-check-user-exists.response";
import userDealerListRequestFactory from "./dealer/list/user-dealer-list-request.factory";
import UserDealerListRequest, {
  UserDealerListRequestQueryParams,
} from "./dealer/list/user-dealer-list.request";
import UserDealerListResponse from "./dealer/list/user-dealer-list.response";
import UserDeleteRequest from "./delete/user-delete.request";
import UserDispatcherEditFormRequest from "./dispatcher/edit-form/user-dispatcher-edit-form.request";
import UserDispatcherEditFormResponse from "./dispatcher/edit-form/user-dispatcher-edit-form.response";
import userDispatcherListRequestFactory from "./dispatcher/list/user-dispatcher-list-request.factory";
import UserDispatcherListRequest, {
  UserDispatcherListRequestQueryParams,
} from "./dispatcher/list/user-dispatcher-list.request";
import UserDispatcherListResponse from "./dispatcher/list/user-dispatcher-list.response";
import userDispatcherUpdateRequestFactory from "./dispatcher/update/user-dispatcher-update-request.factory";
import UserDispatcherUpdateRequest, {
  UserDispatcherUpdateRequestBody,
} from "./dispatcher/update/user-dispatcher-update.request";
import UserDispatcherUpdateResponse from "./dispatcher/update/user-dispatcher-update.response";
import userDriverDownloadRequestFactory from "./driver/download/user-driver-download-request.factory";
import UserDriverDownloadRequest, {
  UserDriverDownloadRequestQueryParams,
} from "./driver/download/user-driver-download.request";
import UserDriverDownloadResponse from "./driver/download/user-driver-download.response";
import UserDriverEditFormRequest from "./driver/edit-form/user-driver-edit-form.request";
import UserDriverEditFormResponse from "./driver/edit-form/user-driver-edit-form.response";
import UserDriverEditNotesRequest from "./driver/edit-notes/user-driver-edit-notes.request";
import userDriverEditNotesRequestFactory from "./driver/edit-notes/user-driver-edit-notes.request.factory";
import UserDriverEditNotesResponse from "./driver/edit-notes/user-driver-edit-notes.response";
import userDriverEditRequestFactory from "./driver/edit/user-driver-edit-request.factory";
import User from "./driver/edit/user-driver-edit.request";
import userDriverListRequestFactory from "./driver/list/user-driver-list-request.factory";
import UserDriverListRequest, {
  UserDriverListRequestQueryParams,
} from "./driver/list/user-driver-list.request";
import UserDriverListResponse from "./driver/list/user-driver-list.response";
import userOperatorListRequestFactory from "./operator/list/user-operator-list-request.factory";
import UserOperatorListRequest, {
  UserOperatorListRequestQueryParams,
} from "./operator/list/user-operator-list.request";
import UserOperatorListResponse from "./operator/list/user-operator-list.response";
import userPassengerListRequestFactory from "./passenger/list/user-passenger-list-request.factory";
import UserPassengerListRequest, {
  UserPassengerListRequestQueryParams,
} from "./passenger/list/user-passenger-list.request";
import UserPassengerListResponse from "./passenger/list/user-passenger-list.response";
import UserPasswordChangeRequest from "./password-change/user-password-change-request";
import userPasswordChangeRequestFactory from "./password-change/user-password-change-request.factory";
import UserPasswordChangeResponse from "./password-change/user-password-change-response";
import userRailyOfficerListRequestFactory from "./raily-officer/list/user-raily-officer-list-request.factory";
import UserRailyOfficerListRequest, {
  UserRailyOfficerListRequestQueryParams,
} from "./raily-officer/list/user-raily-officer-list.request";
import UserRailyOfficerListResponse from "./raily-officer/list/user-raily-officer-list.response";
import UserRestoreRequest from "./restore/user-restore.request";
import UserRestoreResponse from "./restore/user-restore.response";
import userTaxiOfficerListRequestFactory from "./taxi-officer/list/user-taxi-officer-list-request.factory";
import UserTaxiOfficerListRequest, {
  UserTaxiOfficerListRequestQueryParams,
} from "./taxi-officer/list/user-taxi-officer-list.request";
import UserTaxiOfficerListResponse from "./taxi-officer/list/user-taxi-officer-list.response";

const userApiService = () => {
  const checkUserExists = (
    request: UserCheckUserExistsRequest,
    abortSignal: AbortSignal,
  ): Promise<UserCheckUserExistsResponse> => {
    const url = `/users/exists/${request.username}`;

    return railyApiService().get({
      url,
      abortSignal,
    });
  };

  const deleteUser = (
    request: UserDeleteRequest,
    abortSignal: AbortSignal,
  ): Promise<HttpResponse> => {
    const url = `/users/${request.userUuid}`;

    return railyApiService().delete({
      url,
      abortSignal,
    });
  };

  const restore = (
    request: UserRestoreRequest,
    abortSignal: AbortSignal,
  ): Promise<UserRestoreResponse> => {
    const url = `/users/${request.userUuid}/restore`;

    return railyApiService().put<UserRestoreResponse>({
      url,
      abortSignal,
    });
  };

  const changePassword = (
    request: UserPasswordChangeRequest,
    abortSignal: AbortSignal,
  ): Promise<UserPasswordChangeResponse> => {
    const url = `/users/me/password/change`;

    const body = userPasswordChangeRequestFactory.createBody(request);

    return railyApiService().put({
      url,
      abortSignal,
      body,
    });
  };

  const getCargoOfficerList = async (
    request: UserCargoOfficerListRequest,
    abortSignal: AbortSignal,
  ): Promise<UserCargoOfficerListResponse> => {
    const url = `/users/all/cargo-officers`;

    const queryParams: UserCargoOfficerListRequestQueryParams =
      userCargoOfficerListRequestFactory.createQueryParams(request);

    return railyApiService().get<UserCargoOfficerListResponse>({
      url,
      abortSignal,
      queryParams,
    });
  };

  const getDealerList = async (
    request: UserDealerListRequest,
    abortSignal: AbortSignal,
  ): Promise<UserDealerListResponse> => {
    const url = `/users/all/dealers`;

    const queryParams: UserDealerListRequestQueryParams =
      userDealerListRequestFactory.createQueryParams(request);

    return railyApiService().get<UserDealerListResponse>({
      url,
      abortSignal,
      queryParams,
    });
  };

  const getDispatcherList = async (
    request: UserDispatcherListRequest,
    abortSignal: AbortSignal,
  ): Promise<UserDispatcherListResponse> => {
    const url = `/users/all/dispatchers`;

    const queryParams: UserDispatcherListRequestQueryParams =
      userDispatcherListRequestFactory.createQueryParams(request);

    return railyApiService().get<UserDispatcherListResponse>({
      url,
      abortSignal,
      queryParams,
    });
  };

  const updateDispatcher = (
    request: UserDispatcherUpdateRequest,
    abortSignal: AbortSignal,
  ): Promise<UserDispatcherUpdateResponse> => {
    const url = `/users/${request.dispatcherId}/dispatcher`;

    const body: UserDispatcherUpdateRequestBody =
      userDispatcherUpdateRequestFactory.createBody(request);

    return railyApiService().put<UserDispatcherUpdateResponse>({
      url,
      abortSignal,
      body,
    });
  };

  const getDispatcherEditForm = (
    request: UserDispatcherEditFormRequest,
    abortSignal: AbortSignal,
  ): Promise<UserDispatcherEditFormResponse> => {
    const url = `/users/${request.dispatcherId}/dispatcher/edit-form`;

    return railyApiService().get<UserDispatcherEditFormResponse>({
      url,
      abortSignal,
    });
  };

  const getDriverList = async (
    request: UserDriverListRequest,
    abortSignal: AbortSignal,
  ): Promise<UserDriverListResponse> => {
    const url = `/users/all/drivers`;

    const queryParams: UserDriverListRequestQueryParams =
      userDriverListRequestFactory.createQueryParams(request);

    return railyApiService().get<UserDriverListResponse>({
      url,
      abortSignal,
      queryParams,
    });
  };

  const downloadDriver = async (
    request: UserDriverDownloadRequest,
    abortSignal: AbortSignal,
  ): Promise<UserDriverDownloadResponse> => {
    const url = `/users/all/drivers/csv-dump`;

    const queryParams: UserDriverDownloadRequestQueryParams =
      userDriverDownloadRequestFactory.createQueryParams(request);

    return railyApiService().getFile({
      url,
      abortSignal,
      queryParams,
    });
  };

  const editDriver = async (
    request: User,
    abortSignal: AbortSignal,
    driverUuid: string,
  ): Promise<HttpResponse<string>> => {
    const url = `/users/${driverUuid}/driver`;
    const body = userDriverEditRequestFactory.createBody(request);

    return railyApiService().put({
      url,
      abortSignal,
      body,
    });
  };

  const editDriverNotes = async (
    request: UserDriverEditNotesRequest,
    abortSignal: AbortSignal,
  ): Promise<UserDriverEditNotesResponse> => {
    const url = `/users/${request.userUuid}/driver/notes`;
    const body = userDriverEditNotesRequestFactory.createBody(request);

    return railyApiService().put({
      url,
      abortSignal,
      body,
    });
  };

  const getDriverEditForm = async (
    request: UserDriverEditFormRequest,
    abortSignal: AbortSignal,
  ): Promise<UserDriverEditFormResponse> => {
    const url = `/users/${request.id}/driver/edit-form`;

    return railyApiService().get<UserDriverEditFormResponse>({
      url,
      abortSignal,
    });
  };

  const getOperatorList = async (
    request: UserOperatorListRequest,
    abortSignal: AbortSignal,
  ): Promise<UserOperatorListResponse> => {
    const url = `/users/all/operators`;

    const queryParams: UserOperatorListRequestQueryParams =
      userOperatorListRequestFactory.createQueryParams(request);

    return railyApiService().get<UserOperatorListResponse>({
      url,
      queryParams,
      abortSignal,
    });
  };

  const getPassengerList = async (
    request: UserPassengerListRequest,
    abortSignal: AbortSignal,
  ): Promise<UserPassengerListResponse> => {
    const url = `/users/all/passengers`;

    const queryParams: UserPassengerListRequestQueryParams =
      userPassengerListRequestFactory.createQueryParams(request);

    return railyApiService().get<UserPassengerListResponse>({
      url,
      abortSignal,
      queryParams,
    });
  };

  const getRailyOfficerList = async (
    request: UserRailyOfficerListRequest,
    abortSignal: AbortSignal,
  ): Promise<UserRailyOfficerListResponse> => {
    const url = `/users/all/raily-officers`;

    const queryParams: UserRailyOfficerListRequestQueryParams =
      userRailyOfficerListRequestFactory.createQueryParams(request);

    return railyApiService().get<UserRailyOfficerListResponse>({
      url,
      queryParams,
      abortSignal,
    });
  };

  const getTaxiOfficerList = async (
    request: UserTaxiOfficerListRequest,
    abortSignal: AbortSignal,
  ): Promise<UserTaxiOfficerListResponse> => {
    const url = `/users/all/taxi-officers`;

    const queryParams: UserTaxiOfficerListRequestQueryParams =
      userTaxiOfficerListRequestFactory.createQueryParams(request);

    return railyApiService().get<UserTaxiOfficerListResponse>({
      url,
      abortSignal,
      queryParams,
    });
  };

  return {
    checkUserExists,
    deleteUser,
    restore,
    changePassword,
    getCargoOfficerList,
    getDealerList,
    getDispatcherList,
    updateDispatcher,
    getDispatcherEditForm,
    getDriverList,
    downloadDriver,
    editDriver,
    editDriverNotes,
    getDriverEditForm,
    getOperatorList,
    getPassengerList,
    getRailyOfficerList,
    getTaxiOfficerList,
  };
};

export default userApiService;
